// ============================================================================
// TeamLogoEditor — upload / previsualisation / remplacement logo equipe (PDR-EQU-03)
// ============================================================================

import { useRef, useState } from 'react';
import { ConfirmDialog, GS_COMPONENTS } from '@gs/ui';
import { ImagePlus, Trash2 } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useBlobObjectUrl } from '../../lib/blobs/objectUrl';
import { useRemoveTeamLogo, useUploadTeamLogo, teamErrorCode } from '../../hooks/useTeams';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_CLIENT_BYTES = 2 * 1024 * 1024;

export function TeamLogoEditor({
  teamId,
  hasLogo,
  canWrite,
  onError,
}: {
  teamId: string;
  hasLogo: boolean;
  canWrite: boolean;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadTeamLogo(teamId);
  const remove = useRemoveTeamLogo(teamId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const logoUrl = useBlobObjectUrl(hasLogo ? `/api/teams/${teamId}/logo/content` : null);

  function onPick(file: File | undefined): void {
    if (!file) return;
    if (file.size > MAX_CLIENT_BYTES) {
      onError('too_large');
      return;
    }
    if (!ACCEPT.split(',').includes(file.type)) {
      onError('type_refused');
      return;
    }
    const local = URL.createObjectURL(file);
    setPreviewUrl(local);
    upload.mutate(file, {
      onSuccess: () => {
        URL.revokeObjectURL(local);
        setPreviewUrl(null);
      },
      onError: (err) => {
        URL.revokeObjectURL(local);
        setPreviewUrl(null);
        onError(teamErrorCode(err));
      },
    });
  }

  const displaySrc = previewUrl ?? logoUrl;

  return (
    <div className={`${GS_COMPONENTS.card} flex flex-wrap items-center gap-3 p-3.5`}>
      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-700">
        {displaySrc ? (
          <img src={displaySrc} alt="" className="h-full w-full object-cover" />
        ) : (
          <ImagePlus size={22} aria-hidden="true" className="text-slate-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-field font-medium text-slate-800 dark:text-slate-100">
          {t('gest:equipes.logo.title')}
        </p>
        <p className="text-meta text-slate-500 dark:text-slate-400">
          {t('gest:equipes.logo.hint')}
        </p>
      </div>
      {canWrite ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            disabled={upload.isPending}
            onClick={() => inputRef.current?.click()}
            className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
          >
            {upload.isPending ? t('gest:equipes.logo.uploading') : t('gest:equipes.logo.upload')}
          </button>
          {hasLogo ? (
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => setConfirmOpen(true)}
              className={GS_COMPONENTS.btnDangerOutline}
            >
              <Trash2 size={16} aria-hidden="true" />
              <span className="sr-only">{t('gest:equipes.logo.remove')}</span>
            </button>
          ) : null}
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        title={t('gest:equipes.logo.confirmTitle')}
        message={t('gest:equipes.logo.confirmMessage')}
        confirmLabel={t('gest:equipes.logo.confirmConfirm')}
        cancelLabel={t('gest:equipes.logo.confirmCancel')}
        variant="danger"
        isLoading={remove.isPending}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: () => setConfirmOpen(false),
            onError: (err) => {
              setConfirmOpen(false);
              onError(teamErrorCode(err));
            },
          })
        }
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
